import React, { useState, useCallback } from "react";
import {
  Check,
  Copy,
  AlertCircle,
  ArrowRight,
  Info,
  ExternalLink,
  RefreshCw,
  X,
} from "lucide-react";
import { useTransactionTrackingStore } from "../../store/useTransactionTrackingStore";
import {
  useActiveTransactions,
  useFeatureFlagsByCategory,
} from "../../hooks/query/queries";
import { useTransactionSocket } from "../../hooks/useTransactionSocket";
import { useAuth } from "../../context/AuthContext";
import { cn } from "../../lib/utils";
import { formatDynamicDecimals } from "../../utils/numberFormatter";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogClose,
} from "../ui/dialog";
import { Button } from "../ui/button";

// Transaction timeline steps configuration
const TIMELINE_STEPS = [
  {
    id: "submitted",
    label: "Request submitted",
    description: "Your withdrawal request was received",
    statuses: [
      "Pending",
      "Pending Screening",
      "Queued",
      "Broadcasting",
      "Confirming",
      "Confirmed",
      "Completed",
    ],
  },
  {
    id: "compliance",
    label: "Compliance review",
    description: "Security checks completed",
    statuses: [
      "Pending Screening",
      "Queued",
      "Broadcasting",
      "Confirming",
      "Confirmed",
      "Completed",
    ],
  },
  {
    id: "processing",
    label: "Processing",
    description: "Transaction is being processed",
    warningDescription: "Status update unavailable",
    statuses: [
      "Queued",
      "Broadcasting",
      "Confirming",
      "Confirmed",
      "Completed",
    ],
  },
  {
    id: "broadcasting",
    label: "Broadcasting",
    description: "Transaction is being broadcast to the network",
    statuses: ["Broadcasting", "Confirming", "Confirmed", "Completed"],
  },
  {
    id: "confirming",
    label: "Confirming",
    description: "Awaiting blockchain confirmations",
    statuses: ["Confirming", "Confirmed", "Completed"],
  },
  {
    id: "completed",
    label: "Completed",
    description: "Transaction finalized",
    statuses: ["Completed"],
  },
];

const CRYPTO_COLORS = {
  BTC: "bg-[#F7931A]",
  ETH: "bg-[#627EEA]",
  USDT: "bg-[#26A17B]",
  MATIC: "bg-[#8247E5]",
  SOL: "bg-[#9945FF]",
  XRP: "bg-[#23292F]",
  ADA: "bg-[#0033AD]",
  DOGE: "bg-[#C2A633]",
  AVAX: "bg-[#E84142]",
  ARB: "bg-[#28A0F0]",
};

function formatTimeAgo(date) {
  if (!date) return "";
  const now = new Date();
  const txDate = new Date(date);
  const diffMs = now - txDate;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins} min ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? "s" : ""} ago`;
  return `${diffDays} day${diffDays > 1 ? "s" : ""} ago`;
}

function CryptoIcon({ asset, iconUrl, className }) {
  const [hasError, setHasError] = useState(false);
  const bgColor = CRYPTO_COLORS[asset?.toUpperCase()] || "bg-[#6D6584]";

  if (!iconUrl || hasError) {
    return (
      <div
        className={cn(
          "rounded-full flex items-center justify-center text-white font-semibold text-xs",
          bgColor,
          className
        )}
      >
        {asset?.substring(0, 2).toUpperCase()}
      </div>
    );
  }

  return (
    <img
      src={iconUrl}
      alt={asset}
      className={cn("rounded-full object-cover", className)}
      onError={() => setHasError(true)}
    />
  );
}

function TimelineStep({
  step,
  isCompleted,
  isActive,
  isWarning,
  isLast,
  isPending,
}) {
  return (
    <div
      className={cn("relative flex items-center gap-4", !isLast && "mb-[20px]")}
    >
      {/* Circle container - relative for line positioning */}
      <div className="relative flex-shrink-0">
        {isCompleted ? (
          <div className="w-10 h-10 rounded-full bg-[#17C3A5] flex items-center justify-center">
            <Check className="w-5 h-5 text-white" strokeWidth={3} />
          </div>
        ) : isActive ? (
          /* Active state - current step with pulse animation and check (always orange) */
          <div className="relative w-10 h-10 flex items-center justify-center">
            <div
              className="absolute inset-0 rounded-full bg-[#FF9500]"
              style={{
                animation: "timeline-ping 1.5s ease-in infinite",
              }}
            />
            <div className="relative w-10 h-10 rounded-full flex items-center justify-center bg-[#FF9500]">
              <Check className="w-5 h-5 text-white" strokeWidth={3} />
            </div>
          </div>
        ) : (
          /* Pending state - gray filled circle with white inner circle */
          <div className="w-10 h-10 rounded-full bg-[#9D96AF] flex items-center justify-center">
            <div className="w-[14px] h-[14px] rounded-full bg-white" />
          </div>
        )}
        {/* Connecting line - absolutely positioned from bottom of circle */}
        {!isLast && (
          <div
            className={cn(
              "absolute left-1/2 -translate-x-1/2 top-full w-[3px] h-[38px] transition-all",
              isCompleted ? "bg-[#17C3A5]" : "bg-[#9D96AF]"
            )}
          />
        )}
      </div>

      {/* Step content */}
      <div className="py-2 flex-1">
        <p
          className={cn(
            "font-semibold text-[16px] leading-[24px]",
            isPending ? "text-[#9D96AF]" : "text-[#423E51]"
          )}
        >
          {step.label}
        </p>
        <p
          className={cn(
            "text-[12px] leading-[14px] mt-1",
            isPending ? "text-[#9D96AF]" : "text-[#6D6584]"
          )}
        >
          {isWarning && step.warningDescription
            ? step.warningDescription
            : step.description}
        </p>
      </div>
    </div>
  );
}

function TransactionItem({
  transaction,
  isExpanded,
  onToggle,
  isFirst,
  onRefresh,
  isRefreshing,
}) {
  const [copied, setCopied] = useState(false);
  const [showAllSteps, setShowAllSteps] = useState(false);
  const [animationState, setAnimationState] = useState(
    isExpanded ? "expanded" : "collapsed"
  );
  const [shouldRender, setShouldRender] = useState(isExpanded);

  // Handle expand/collapse with animation
  React.useEffect(() => {
    if (isExpanded) {
      setShouldRender(true);
      // Small delay to ensure DOM is ready before animating
      requestAnimationFrame(() => {
        setAnimationState("expanding");
      });
    } else {
      // Only collapse if we were previously expanded
      setAnimationState((prev) => (prev !== "collapsed" ? "collapsing" : prev));
    }
  }, [isExpanded]);

  // Handle animation end to clean up collapsed state
  const handleAnimationEnd = () => {
    if (animationState === "expanding") {
      setAnimationState("expanded");
    } else if (animationState === "collapsing") {
      setAnimationState("collapsed");
      setShouldRender(false);
    }
  };

  const isDeposit =
    transaction.transactionType === "receive" ||
    transaction.transactionType === "deposit";
  const assetName = transaction.cryptoAsset?.toUpperCase() || "Crypto";
  const transactionLabel = `${assetName} ${isDeposit ? "Deposit" : "Withdrawal"}`;

  // Format: "0.75 ETH • Ethereum (ERC-20)"
  // Handle chain being either a string, object with chain property, or null
  const getChainName = () => {
    let rawChain = "";
    if (transaction.chain) {
      if (typeof transaction.chain === "string") rawChain = transaction.chain;
      else if (transaction.chain.chain) rawChain = transaction.chain.chain;
    }
    if (!rawChain) {
      rawChain = transaction.network || transaction.chainName || "";
    }
    // Format: "BTC_NETWORK" -> "BTC Network", "ETHEREUM" -> "Ethereum"
    return rawChain
      .replace(/_/g, " ")
      .split(" ")
      .map((word) => {
        const lower = word.toLowerCase();
        // Keep crypto symbols uppercase (btc, eth, etc.)
        if (
          [
            "btc",
            "eth",
            "usdt",
            "usdc",
            "matic",
            "sol",
            "xrp",
            "ada",
            "avax",
            "arb",
            "erc",
          ].includes(lower)
        ) {
          return word.toUpperCase();
        }
        // Title case for other words
        return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
      })
      .join(" ");
  };
  const chainName = getChainName();
  const amountWithAsset = `${formatDynamicDecimals(parseFloat(transaction.amount), transaction.cryptoAsset)} ${assetName}`;
  const networkLabel = chainName
    ? `${amountWithAsset} • ${chainName}`
    : amountWithAsset;

  // Determine which steps are completed based on status
  const getStepStatus = (step, stepIndex) => {
    const currentStepIndex = TIMELINE_STEPS.findIndex((s, idx) => {
      const nextStep = TIMELINE_STEPS[idx + 1];
      return (
        s.statuses.includes(transaction.status) &&
        (!nextStep || !nextStep.statuses.includes(transaction.status))
      );
    });

    const isCompleted =
      stepIndex < currentStepIndex ||
      (stepIndex === currentStepIndex && transaction.status === "Completed");
    const isActive =
      stepIndex === currentStepIndex && transaction.status !== "Completed";
    const isPending = stepIndex > currentStepIndex;
    const isWarning = isActive && transaction.statusUnavailable;

    return { isCompleted, isActive, isPending, isWarning };
  };

  const handleCopy = async (text) => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const isFailed =
    transaction.status === "Failed" || transaction.status === "Rejected";
  const isActive = !["Completed", "Failed", "Rejected"].includes(
    transaction.status
  );

  // Border pulse for the first active transaction (only when collapsed)
  const showBorderPulse = isFirst && isActive && !isExpanded;

  return (
    <>
      {/* Inject keyframes for animations */}
      <style>
        {`
          @keyframes timeline-ping {
            0% { transform: scale(1); opacity: 0.5; }
            100% { transform: scale(1.4); opacity: 0; }
          }
          @keyframes border-pulse {
            0% {
              border-color: rgba(23, 195, 165, 0.3);
              box-shadow: 0 0 0 0 rgba(23, 195, 165, 0.4);
            }
            100% {
              border-color: rgba(23, 195, 165, 1);
              box-shadow: 0 0 0 12px rgba(23, 195, 165, 0);
            }
          }
        `}
      </style>
      <div
        className="border rounded-[16px] overflow-hidden bg-white"
        style={
          showBorderPulse
            ? { animation: "border-pulse 2s linear infinite" }
            : { borderColor: "#EBE9EF" }
        }
      >
        {/* Transaction header - clickable */}
        <button
          onClick={onToggle}
          className="w-full flex items-center justify-between p-4 hover:bg-[#FAFAFA] transition-colors"
        >
          <div className="flex items-center gap-3">
            <CryptoIcon
              asset={transaction.cryptoAsset}
              iconUrl={transaction.assetIcon}
              className="w-8 h-8"
            />
            <div className="text-left">
              <p className="font-semibold text-[16px] leading-[24px] text-[#423E51]">
                {transactionLabel}
              </p>
              <p className="text-[12px] leading-[14px] text-[#6D6584]">
                {isExpanded
                  ? formatTimeAgo(transaction.createdAt)
                  : networkLabel}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            {isExpanded && (
              <p className="font-semibold text-[16px] leading-[24px] text-[#423E51]">
                {amountWithAsset}
              </p>
            )}
            <span
              className={cn(
                "text-[10px] font-bold uppercase tracking-[0.5px] leading-[14px]",
                isActive
                  ? "text-[#17C3A5]"
                  : isFailed
                    ? "text-[#EB3366]"
                    : "text-[#17C3A5]"
              )}
            >
              {isActive ? "ACTIVE" : transaction.status?.toUpperCase()}
            </span>
          </div>
        </button>

        {/* Expanded content with animation */}
        {shouldRender && (
          <div
            className={cn(
              "grid motion-reduce:transition-none",
              animationState === "expanding" && "animate-expand-content",
              animationState === "collapsing" && "animate-collapse-content",
              animationState === "expanded" && "grid-rows-[1fr]",
              animationState === "collapsed" && "grid-rows-[0fr]"
            )}
            onAnimationEnd={handleAnimationEnd}
          >
            <div className="overflow-hidden">
              <div className="px-4 pb-6">
                {/* Divider */}
                <div className="h-[1px] bg-[#EBE9EF] mb-4" />

                {/* Timeline */}
                {isFailed ? (
                  <div className="flex items-start gap-3 p-4 bg-red-50 rounded-lg mb-4">
                    <AlertCircle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-red-700">
                        Transaction {transaction.status}
                      </p>
                      <p className="text-sm text-red-600 mt-1">
                        {transaction.failureReason ||
                          "Please contact support for more information."}
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="mb-4">
                    {(() => {
                      // Find current step index
                      const currentStepIndex = TIMELINE_STEPS.findIndex(
                        (s, idx) => {
                          const nextStep = TIMELINE_STEPS[idx + 1];
                          return (
                            s.statuses.includes(transaction.status) &&
                            (!nextStep ||
                              !nextStep.statuses.includes(transaction.status))
                          );
                        }
                      );

                      // Show last 3 steps around current position if not expanded
                      let stepsToShow = TIMELINE_STEPS;
                      let startIndex = 0;
                      if (!showAllSteps && TIMELINE_STEPS.length > 3) {
                        // Show steps around the current step, ensuring we show 3 steps
                        startIndex = Math.max(
                          0,
                          Math.min(
                            currentStepIndex - 1,
                            TIMELINE_STEPS.length - 3
                          )
                        );
                        stepsToShow = TIMELINE_STEPS.slice(
                          startIndex,
                          startIndex + 3
                        );
                      }

                      return (
                        <>
                          {stepsToShow.map((step, idx) => {
                            const actualIndex = showAllSteps
                              ? idx
                              : startIndex + idx;
                            const {
                              isCompleted,
                              isActive,
                              isPending,
                              isWarning,
                            } = getStepStatus(step, actualIndex);
                            const isLastVisible =
                              idx === stepsToShow.length - 1;
                            return (
                              <TimelineStep
                                key={step.id}
                                step={step}
                                isCompleted={isCompleted}
                                isActive={isActive}
                                isPending={isPending}
                                isWarning={isWarning}
                                isLast={isLastVisible}
                              />
                            );
                          })}
                          {/* Expand/Collapse button */}
                          {TIMELINE_STEPS.length > 3 && (
                            <button
                              onClick={() => setShowAllSteps(!showAllSteps)}
                              className="flex items-center gap-2 mt-2 ml-14 text-[14px] font-semibold text-[#6D6584] hover:text-[#423E51] transition-colors"
                            >
                              {showAllSteps ? "Show less" : "Show all steps"}
                            </button>
                          )}
                        </>
                      );
                    })()}
                  </div>
                )}

                {/* Status unavailable message */}
                {transaction.statusUnavailable && !isFailed && (
                  <div className="flex items-start gap-3 p-4 rounded-[18px] mb-4">
                    <div className="w-10 h-10 rounded-full bg-[#F5F4F7] flex items-center justify-center shrink-0">
                      <Info className="w-5 h-5 text-[#9D96AF]" />
                    </div>
                    <div className="pt-1">
                      <p className="font-semibold text-[16px] leading-[24px] text-[#9D96AF]">
                        We can't fetch live updates right now
                      </p>
                      <p className="text-[12px] leading-[14px] text-[#9D96AF] mt-1">
                        Your transaction may still be progressing. Try again in
                        a few moments to see the latest status.
                      </p>
                    </div>
                  </div>
                )}

                {/* Transaction Hash & Explorer Link - show for all states */}
                <div className="mb-6">
                  <p className="text-[14px] leading-[20px] text-[#6D6584] mb-3">
                    Transaction Hash
                  </p>
                  <div className="flex items-center gap-3">
                    {/* Explorer link */}
                    {transaction.explorerUrl && transaction.transactionHash ? (
                      <a
                        href={`${transaction.explorerUrl}${transaction.explorerTxPath || "/tx/"}${transaction.transactionHash}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="shrink-0 h-12 px-4 flex items-center justify-center gap-2 border border-[#EBE9EF] rounded-full hover:bg-[#F5F4F7] transition-colors text-[14px] font-semibold text-[#6D6584]"
                      >
                        <ExternalLink className="w-4 h-4" />
                        View Explorer
                      </a>
                    ) : (
                      <div className="shrink-0 h-12 px-4 flex items-center justify-center gap-2 border border-[#EBE9EF] rounded-full bg-[#F5F4F7] text-[14px] font-semibold text-[#9D96AF] cursor-not-allowed">
                        <ExternalLink className="w-4 h-4" />
                        View Explorer
                      </div>
                    )}
                    {/* Hash display */}
                    <div className="flex-1 flex items-center justify-between gap-3 px-4 h-12 bg-white border border-[#CECBD7] rounded-full">
                      <p className="text-[14px] leading-[20px] text-[#9D96AF] font-mono truncate">
                        {transaction.transactionHash || "Awaiting hash..."}
                      </p>
                      {transaction.transactionHash ? (
                        <button
                          onClick={() =>
                            handleCopy(transaction.transactionHash)
                          }
                          className="shrink-0 w-8 h-8 flex items-center justify-center border border-[#EBE9EF] rounded-full hover:bg-[#F5F4F7] transition-colors"
                        >
                          {copied ? (
                            <Check className="w-4 h-4 text-[#17C3A5]" />
                          ) : (
                            <Copy className="w-4 h-4 text-[#6D6584]" />
                          )}
                        </button>
                      ) : (
                        <div className="shrink-0 w-8 h-8 flex items-center justify-center border border-[#EBE9EF] rounded-full bg-[#F5F4F7] cursor-not-allowed">
                          <Copy className="w-4 h-4 text-[#CECBD7]" />
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Action buttons */}
                <div className="flex items-center justify-center gap-4">
                  <button
                    onClick={onToggle}
                    className="h-12 px-6 border border-[#EBE9EF] rounded-full text-[14px] font-semibold text-[#6D6584] hover:bg-[#F5F4F7] transition-colors"
                  >
                    Close
                  </button>
                  <button
                    onClick={onRefresh}
                    disabled={isRefreshing}
                    className={cn(
                      "h-12 px-6 rounded-full text-[14px] font-semibold text-white transition-colors flex items-center gap-2",
                      isRefreshing && "opacity-70 cursor-not-allowed"
                    )}
                    style={{
                      background:
                        "linear-gradient(220deg, #EB3366 13%, #9747FF 130%)",
                    }}
                  >
                    {isRefreshing && (
                      <RefreshCw className="w-4 h-4 animate-spin" />
                    )}
                    {isRefreshing ? "Refreshing..." : "Refresh"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

export default function LiveTransactions() {
  const { isAuthenticated } = useAuth();
  const { data: featureFlags } = useFeatureFlagsByCategory("mainFeatures");
  const isEnabled = isAuthenticated && featureFlags?.liveTransactionTracking;

  const [expandedId, setExpandedId] = useState(null);
  const [showAllModal, setShowAllModal] = useState(false);
  const [modalExpandedId, setModalExpandedId] = useState(null);

  // Initialize WebSocket connection
  useTransactionSocket(isEnabled);

  // Fetch active transactions with refetch capability
  const { refetch, isFetching } = useActiveTransactions();

  const activeTransactions =
    useTransactionTrackingStore((s) => s.activeTransactions) || [];

  // Show max 5 transactions in the main view
  const MAX_VISIBLE = 5;
  const visibleTransactions = activeTransactions.slice(0, MAX_VISIBLE);

  // Refresh handler - refetches transaction data without full page reload
  const handleRefresh = useCallback(async () => {
    if (isFetching) return; // Debounce: prevent multiple requests
    try {
      await refetch();
    } catch (error) {
      console.error("Failed to refresh transactions:", error);
    }
  }, [refetch, isFetching]);

  if (!isEnabled || activeTransactions.length === 0) {
    return null;
  }

  return (
    <div className="bg-white rounded-[24px] border border-[#EBE9EF] p-6 overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-[20px] font-semibold leading-[28px] text-[#6D6584]">
          Live Transactions
        </h2>
        <div className="flex items-center gap-3">
          {/* Refresh button */}
          <button
            onClick={handleRefresh}
            disabled={isFetching}
            className={cn(
              "w-8 h-8 flex items-center justify-center rounded-full border border-[#EBE9EF] transition-colors",
              isFetching
                ? "opacity-50 cursor-not-allowed"
                : "hover:bg-[#F5F4F7]"
            )}
            title="Refresh transactions"
          >
            <RefreshCw
              className={cn(
                "w-4 h-4 text-[#6D6584]",
                isFetching && "animate-spin"
              )}
            />
          </button>
          <Button
            onClick={() => setShowAllModal(true)}
            variant="ghost"
            className="text-[#423E51] flex items-center space-x-2 text-[14px] font-semibold rounded-full hover:bg-[#F5F4F7]"
          >
            <span>View All</span>
            <ArrowRight className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Transaction list */}
      <div className="space-y-2">
        {visibleTransactions.map((tx, index) => (
          <TransactionItem
            key={tx._id}
            transaction={tx}
            isExpanded={expandedId === tx._id}
            onToggle={() =>
              setExpandedId(expandedId === tx._id ? null : tx._id)
            }
            isFirst={index === 0}
            onRefresh={handleRefresh}
            isRefreshing={isFetching}
          />
        ))}
      </div>

      {/* View All Modal */}
      <Dialog open={showAllModal} onOpenChange={setShowAllModal}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <div className="flex items-center justify-between">
              <DialogTitle className="text-[20px] font-semibold text-[#423E51]">
                All Live Transactions ({activeTransactions.length})
              </DialogTitle>
              <DialogClose className="rounded-full p-2 hover:bg-[#F5F4F7] transition-colors">
                <X className="w-5 h-5 text-[#6D6584]" />
              </DialogClose>
            </div>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto space-y-2 pr-2">
            {activeTransactions.map((tx, index) => (
              <TransactionItem
                key={tx._id}
                transaction={tx}
                isExpanded={modalExpandedId === tx._id}
                onToggle={() =>
                  setModalExpandedId(modalExpandedId === tx._id ? null : tx._id)
                }
                isFirst={index === 0}
                onRefresh={handleRefresh}
                isRefreshing={isFetching}
              />
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
